import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import {
  EnvironmentId,
  PortSchema,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

export const ExecutionEnvironmentPlatformOs = Schema.Literals([
  "darwin",
  "linux",
  "windows",
  "unknown",
]);
export type ExecutionEnvironmentPlatformOs = typeof ExecutionEnvironmentPlatformOs.Type;

export const ExecutionEnvironmentPlatformArch = Schema.Literals(["arm64", "x64", "other"]);
export type ExecutionEnvironmentPlatformArch = typeof ExecutionEnvironmentPlatformArch.Type;

export const ExecutionEnvironmentPlatform = Schema.Struct({
  os: ExecutionEnvironmentPlatformOs,
  arch: ExecutionEnvironmentPlatformArch,
});
export type ExecutionEnvironmentPlatform = typeof ExecutionEnvironmentPlatform.Type;

/**
 * The glyph a surface draws for one Environment's machine.
 *
 * Adapted from T3 Code, which detects a specific chassis server-side and lets the user override
 * it. AFAR's descriptor carries only `platform.os`, so the kind is derived from that and `server`
 * is the honest answer for a machine this client cannot identify.
 */
export const EnvironmentMachineKind = Schema.Literals(["server", "linux", "mac", "windows"]);
export type EnvironmentMachineKind = typeof EnvironmentMachineKind.Type;

/**
 * A descriptor this client has not received yet resolves to the generic `server` rather than to
 * a guess, so a row never flickers between glyphs as a connection comes up.
 */
export function resolveEnvironmentMachineKind(
  descriptor: Pick<ExecutionEnvironmentDescriptor, "platform"> | null | undefined,
): EnvironmentMachineKind {
  switch (descriptor?.platform.os) {
    case "darwin":
      return "mac";
    case "linux":
      return "linux";
    case "windows":
      return "windows";
    case "unknown":
    case undefined:
      return "server";
  }
}

export const ExecutionEnvironmentCapabilities = Schema.Struct({
  repositoryIdentity: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(false))),
  /** Additive: the Environment keeps a user-picked machine glyph in its `environmentIcon` setting. */
  environmentIcon: Schema.optionalKey(Schema.Boolean),
  /** Additive per-model price overrides applied to this Environment's usage. */
  usagePriceOverrides: Schema.optionalKey(Schema.Boolean),
  /** Additive server-first shell synchronization path; absence requires the legacy stream. */
  shellBootstrap: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive byte/count bounded thread-detail and opaque older-history pagination path. */
  threadHistory: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive workspace Pull Request list and independently loaded statistics RPCs. */
  pullRequests: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive on-demand read of one pull request's own checks, for a listing row. */
  pullRequestChecksDetail: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive active-only Pull Request Summary RPC with current viewer authority. */
  pullRequestCheckout: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestReview: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestMerge: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestLifecycle: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestDiscussion: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestEdit: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestSummary: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive active-only Pull Request activity, commits, comments, and review threads RPC. */
  pullRequestCode: Schema.optionalKey(TrimmedNonEmptyString),
  pullRequestTimeline: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive thread pull request collection: durable links, dismissal, and stack discovery. */
  threadPullRequests: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive thread.pin / thread.unpin commands and the sidebar's pinned block. */
  threadPinning: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive thread.pin.reorder command and the orderKey on thread.pin. */
  threadPinReorder: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive thread.active.reorder command: manual order within the Active list. */
  threadActiveReorder: Schema.optionalKey(TrimmedNonEmptyString),
  /**
   * Additive: this Environment runs the Collaborative Browser itself and streams
   * it to every client (ADR-0137). Absent where no Chromium is staged.
   */
  collaborativeBrowser: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive: this Environment is a Cloud Workspace whose Cloud Display can be watched. */
  cloudDisplay: Schema.optionalKey(TrimmedNonEmptyString),
  /** Additive: this Cloud Workspace accepts `server.setCloudClientAway` from its clients. */
  cloudClientAway: Schema.optionalKey(TrimmedNonEmptyString),
});
export type ExecutionEnvironmentCapabilities = typeof ExecutionEnvironmentCapabilities.Type;

export const ExecutionEnvironmentDescriptor = Schema.Struct({
  environmentId: EnvironmentId,
  label: TrimmedNonEmptyString,
  platform: ExecutionEnvironmentPlatform,
  serverVersion: TrimmedNonEmptyString,
  /** The executable release when a Cloud Workspace reports the client version for display. */
  executableVersion: Schema.optionalKey(TrimmedNonEmptyString),
  lifecycleGeneration: Schema.optionalKey(TrimmedNonEmptyString),
  lifecycleState: Schema.optionalKey(Schema.Literals(["ready", "blocked"])),
  listener: Schema.optionalKey(
    Schema.Struct({
      host: TrimmedNonEmptyString,
      port: PortSchema,
    }),
  ),
  capabilities: ExecutionEnvironmentCapabilities,
});
export type ExecutionEnvironmentDescriptor = typeof ExecutionEnvironmentDescriptor.Type;

export const EnvironmentConnectionState = Schema.Literals([
  "connecting",
  "connected",
  "disconnected",
  "error",
]);
export type EnvironmentConnectionState = typeof EnvironmentConnectionState.Type;

export const RepositoryIdentityLocator = Schema.Struct({
  source: Schema.Literal("git-remote"),
  remoteName: TrimmedNonEmptyString,
  remoteUrl: TrimmedNonEmptyString,
});
export type RepositoryIdentityLocator = typeof RepositoryIdentityLocator.Type;

export const RepositoryIdentity = Schema.Struct({
  canonicalKey: TrimmedNonEmptyString,
  locator: RepositoryIdentityLocator,
  rootPath: Schema.optionalKey(TrimmedNonEmptyString),
  displayName: Schema.optionalKey(TrimmedNonEmptyString),
  provider: Schema.optionalKey(TrimmedNonEmptyString),
  owner: Schema.optionalKey(TrimmedNonEmptyString),
  name: Schema.optionalKey(TrimmedNonEmptyString),
});
export type RepositoryIdentity = typeof RepositoryIdentity.Type;

export const ScopedProjectRef = Schema.Struct({
  environmentId: EnvironmentId,
  projectId: ProjectId,
});
export type ScopedProjectRef = typeof ScopedProjectRef.Type;

export const ScopedThreadRef = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
});
export type ScopedThreadRef = typeof ScopedThreadRef.Type;

export const ScopedThreadSessionRef = Schema.Struct({
  environmentId: EnvironmentId,
  threadId: ThreadId,
});
export type ScopedThreadSessionRef = typeof ScopedThreadSessionRef.Type;
